import { Input, Directive } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Subject, combineLatest, take } from 'rxjs';
import { FormMessage } from './pr-form-messages';
import { PrFormConstructor } from './pr-form.contructor';

@Directive()
export abstract class PrFormAbstract {
  @Input() form: UntypedFormGroup = new UntypedFormGroup({}, {});
  @Input() entity: any;

  @Input() derrors = false;
  messageSubject: Subject<FormMessage> = new Subject();

  callback: any;

  loading = false;

  constructor(protected formConstructor: PrFormConstructor) {}

  protected get analyticsService() {
    return this.formConstructor.analyticsService;
  }
  protected get formBuilder() {
    return this.formConstructor.formBuilder;
  }
  protected get prFormService() {
    return this.formConstructor.prFormService;
  }
  protected get utilsService() {
    return this.formConstructor.utilsService;
  }
  protected get notificationService() {
    return this.formConstructor.notificationService;
  }

  emptyIsNull(val) {
    return val == null || val == undefined || val.length === 0 ? null : val;
  }

  /**
   * Valeur initiale du champ `owners` (détenteurs + part de détention `share`,
   * fraction 0..1) pour `<pr-input-owner-share>`.
   * - actif existant avec `owners` : on reprend `{ ownerId, share }`
   *   (share repris s'il existe, sinon réparti 1/n) ;
   * - rien : tableau vide (l'utilisateur ajoute les détenteurs).
   */
  buildInitialOwners(entity: any): Array<{ ownerId: number; share: number }> {
    const owners = entity?.owners;
    if (Array.isArray(owners) && owners.length) {
      const hasShare = owners.some(
        o => o && typeof o === 'object' && o.share != null,
      );
      const n = owners.length;
      const each = Math.round((1 / n) * 100) / 100;
      return owners.map((o, i) => {
        const ownerId = o && typeof o === 'object' ? o.ownerId : o;
        let share: number;
        if (hasShare) {
          share = o && typeof o === 'object' && o.share != null ? o.share : 0;
        } else {
          share =
            i === n - 1
              ? Math.round((1 - each * (n - 1)) * 100) / 100
              : each;
        }
        return { ownerId: Number(ownerId), share };
      });
    }
    return [];
  }

  parseFloat(val) {
    return val == null || val == undefined || val.length === 0
      ? null
      : typeof val == 'number'
        ? parseFloat(val.toString().replace(',', '.'))
        : parseFloat(val.replace(',', '.'));
  }

  initForm(entity: any, options?) {
    this.entity = entity;
    this.form = this.generate(entity, options);
    this.applyDefaultOwners();
  }

  /**
   * À la création, pré-remplit le champ `owners` avec l'utilisateur connecté
   * et une part de 100 %. Ne touche ni aux détenteurs déjà saisis, ni aux
   * actifs existants, et ne force rien si l'utilisateur connecté n'est pas
   * un détenteur du foyer.
   */
  protected applyDefaultOwners() {
    const control = this.form?.get('owners');
    if (!control || this.entity?.id != null) return;
    if (Array.isArray(control.value) && control.value.length) return;

    const userDao = this.formConstructor.userDao;
    combineLatest([userDao.getAuthentified(), userDao.getOwners()])
      .pipe(take(1))
      .subscribe({
        next: ([user, owners]) => {
          if (!user?.id) return;
          if (Array.isArray(control.value) && control.value.length) return;
          if (!(owners || []).some(owner => owner?.id === user.id)) return;
          control.setValue([{ ownerId: Number(user.id), share: 1 }]);
        },
        error: () => null,
      });
  }

  postMessage(message: FormMessage) {
    this.messageSubject.next(message);
  }
  initCallback(callback: any) {
    this.callback = callback;
  }

  postDoneMessage = () => this.postMessage(new FormMessage('DONE'));
  postInfoMessage = () => this.postMessage(new FormMessage('INFO'));
  postRefreshMessage = () => this.postMessage(new FormMessage('REFRESH'));
  postDeleteMessage = message =>
    this.postMessage(new FormMessage('DELETE', message));

  abstract generate(entity: any, options?): UntypedFormGroup;

  abstract extract(): any;

  abstract onSubmit(): any;

  public submit(): any {
    this.displayErrors();

    this.selectErrorInput();

    if (this.form.invalid) return;

    this.onSubmit();
  }

  public displayErrors() {
    this.derrors = true;
  }

  public reset() {
    this.form.reset(this.entity);
  }

  // selectErrorInput() {
  //   setTimeout(() => {
  //     const scrollSection = document.querySelector(
  //       ".pr-form-content"
  //     ) as HTMLElement;
  //     const firstErrorField = document.querySelector(
  //       ".is-invalid"
  //     ) as HTMLElement;
  //     if (!firstErrorField) return;

  //     scrollSection.scrollTop =
  //       firstErrorField.offsetTop -
  //       scrollSection.offsetTop +
  //       scrollSection.scrollTop;

  //     const firstErrorFieldInput = firstErrorField
  //       .children[0] as HTMLInputElement;
  //     if (firstErrorFieldInput) {
  //       firstErrorFieldInput.select();
  //     }
  //   }, 0);
  // }
  selectErrorInput() {
    setTimeout(() => {
      let scrollSection = document.querySelector('.pr-form-content');
      let firstErrorField = document.querySelector(
        '.is-invalid',
      ) as HTMLElement;
      if (!firstErrorField) return;

      if (scrollSection.getBoundingClientRect().top) {
        scrollSection.scrollTop =
          scrollSection.scrollTop -
          scrollSection.getBoundingClientRect().top +
          firstErrorField.getBoundingClientRect().top;
      }
      const firstErrorFieldInput = firstErrorField
        .children[0] as HTMLInputElement;
      // `.select()` n'existe que sur les <input>/<textarea>. Selon le champ en
      // erreur (mat-select, composant custom…), children[0] peut être un autre
      // élément : on garde l'appel défensif pour éviter un crash.
      if (
        firstErrorFieldInput &&
        typeof firstErrorFieldInput.select === 'function'
      ) {
        firstErrorFieldInput.select();
      }
    }, 0);
  }

  sendAnalyticsEvent(formName: string, success: boolean) {
    let changes = success ? this.getChangedFields('', this.form) : 'failure';
    let value = success ? changes.length : -1;
    this.analyticsService.event(
      'submit_form',
      formName,
      JSON.stringify(changes),
      value,
    );
  }

  getChangedFields(prefix: string, form: any) {
    let changedFields = [];
    Object.keys(form.controls).forEach(key => {
      let control = form.controls[key];
      if (control.dirty) {
        if (control.controls)
          changedFields.push(
            ...this.getChangedFields(prefix + key + '.', control),
          );
        else changedFields.push(prefix + key);
      }
    });

    return changedFields;
  }
}
